import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Job, JobCompanyInfo, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';
import { reloadInitialData } from 'store/slices/app';

export const SatNotePages = ['initial', 'terms', 'questions'] as const;

export type SatNotePage = (typeof SatNotePages)[number];

export interface OpenBankingSatNoteContextType {
  job?: Job;
  companyInfo?: JobCompanyInfo;
  page: SatNotePage;
  setPage: React.Dispatch<React.SetStateAction<SatNotePage>>;
  toNextPage: () => void;
  toPrevPage: () => void;
  isLoading: boolean;
}

export const OpenBankingSatNoteContext =
  createContext<OpenBankingSatNoteContextType | null>(null);

export const useOpenBankingSatNoteContext = () =>
  useContext(OpenBankingSatNoteContext) as OpenBankingSatNoteContextType;

export interface OpenBankingSatNoteProviderProps {
  children: ReactNode;
}

const OpenBankingSatNoteProvider: FunctionComponent<OpenBankingSatNoteProviderProps> =
  function ({ children }) {
    const {
      isLoading: statusIsLoading,
      job,
      jobCompanyInfo,
    } = useStatusContext();

    const [page, setPage] = useState<SatNotePage>('initial');

    const dispatch = useAppDispatch();
    const handleViewedError = useApiError('Unknown error viewing sat note');

    const viewed = useMemo(() => {
      if (!job) return false;
      return job?.sat_note_timeline?.viewed_at;
    }, [job]);

    const isLoading = useMemo(
      () => statusIsLoading || !viewed,
      [statusIsLoading, viewed],
    );

    const toNextPage = useCallback(() => {
      const index = SatNotePages.indexOf(page);
      if (index === SatNotePages.length) return;
      const nextPage = SatNotePages[index + 1];
      setPage(nextPage);
    }, [page]);

    const toPrevPage = useCallback(() => {
      const index = SatNotePages.indexOf(page);
      if (index === 0) return;
      const prevPage = SatNotePages[index - 1];
      setPage(prevPage);
    }, [page]);

    const values = useMemo(
      () => ({
        job,
        companyInfo: jobCompanyInfo,
        page,
        setPage,
        toNextPage,
        toPrevPage,
        isLoading,
      }),
      [job, jobCompanyInfo, page, setPage, toNextPage, toPrevPage, isLoading],
    );

    const submitViewedRef = useRef<boolean>(false);
    useEffect(() => {
      if (statusIsLoading || !job || viewed || submitViewedRef.current) return;
      const id = job?.id;
      if (!id) return;
      const hasViewed = job?.sat_note_timeline?.viewed_at;
      if (hasViewed) return;
      submitViewedRef.current = true;
      dispatch(
        actions.viewJobSatNote({
          params: {
            id,
          },
          onError: handleViewedError,
          onSuccess: () => {
            submitViewedRef.current = true;
            dispatch(reloadInitialData());
          },
        }),
      );
    }, [statusIsLoading, job, viewed, handleViewedError, dispatch]);

    return (
      <OpenBankingSatNoteContext.Provider value={values}>
        {children}
      </OpenBankingSatNoteContext.Provider>
    );
  };

export default OpenBankingSatNoteProvider;
